import React, { useState, useCallback } from 'react';
import { Button, Drawer, List, ListItem, ListItemText, Collapse, Autocomplete, TextField, Box } from '@mui/material';
import { MenuBook, ExpandMore, ExpandLess, FolderOutlined } from '@mui/icons-material';
import { map, findIndex } from 'lodash';
import styled from 'styled-components';

const NavigationDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 300px;
    background-color: #f8f9fa;
    border-right: 1px solid #e0e0e0;
  }
`;

const NavigationHeader = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavigationItem = styled(ListItem)`
  cursor: pointer;
  border-radius: 8px;
  margin: 4px 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e3f2fd;
  }
  
  &.active {
    background-color: #1976d2;
    color: white;
    
    svg {
      color: white;
    }
  }
`;

const SubNavigationItem = styled(ListItem)`
  cursor: pointer;
  border-radius: 6px;
  margin: 2px 16px;
  padding-left: 24px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  &.active {
    background-color: #4caf50;
    color: white;
    
    svg {
      color: white;
    }
  }
`;

const ExpandButton = styled.div`
  margin-left: auto;
  padding: 4px;
  cursor: pointer;
`;

const BookNavigation = ({ 
  subTopics = [], 
  pages = [], 
  currentPageId, 
  onNavigateToPage,
  pageId,
  maskId,
  onPageIdChange,
  onMaskIdChange,
  navigationOpen,
  setNavigationOpen
}) => {
  const [expandedUnits, setExpandedUnits] = useState({});

  const toggleUnitExpansion = useCallback((unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  }, []);

  const navigateToPage = useCallback((pageId) => {
    const pageIndex = findIndex(pages, p => p.pageId == pageId);
    if (pageIndex >= 0 && onNavigateToPage) {
      onNavigateToPage(pageId);
      setNavigationOpen(false);
    }
  }, [pages, onNavigateToPage]);

  const isPageInRange = useCallback((startPage, endPage, currentPageId) => {
    const startIndex = findIndex(pages, p => p.pageId == startPage);
    const endIndex = findIndex(pages, p => p.pageId == endPage);
    const rangePages = pages.slice(startIndex, endIndex + 1);
    return rangePages.some(p => p.pageId == currentPageId);
  }, []);

  const renderNavigationMenu = () => {
    if (!subTopics || subTopics.length === 0) {
      return (
        <List>
          <ListItem>
            <ListItemText 
              primary="No sections available" 
              secondary="Add sections to enable navigation"
            />
          </ListItem>
        </List>
      );
    }

    return (
      <List>
        {map(subTopics, (unit, unitIndex) => {
          const hasSubUnits = Array.isArray(unit.subUnits) && unit.subUnits.length > 0;
          const isExpanded = expandedUnits[unit.subTopicId];
          const isUnitActive = isPageInRange(unit.startPage, unit.finishPage, currentPageId);

          return (
            <div key={unit.subTopicId}>
              <NavigationItem
                className={isUnitActive ? 'active' : ''}
                onClick={() => {
                  if (hasSubUnits) {
                    toggleUnitExpansion(unit.subTopicId);
                  } else {
                    navigateToPage(unit.startPage);
                  }
                }}
              >
                <MenuBook sx={{ fontSize: 20, marginRight: 1 }} />
                <ListItemText 
                  primary={unit.shortName || `Section ${unitIndex + 1}`}
                  secondary={`Pages ${unit.startPage}-${unit.finishPage}`}
                />
                {hasSubUnits && (
                  <ExpandButton onClick={(e) => {
                    e.stopPropagation();
                    toggleUnitExpansion(unit.subTopicId);
                  }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ExpandButton>
                )}
              </NavigationItem>
              
              {hasSubUnits && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {map(unit.subUnits, (subUnit, subIndex) => {
                      const isSubUnitActive = isPageInRange(subUnit.start, subUnit.finish, currentPageId);
                      
                      return (
                        <SubNavigationItem
                          key={`${unit.subTopicId}-${subIndex}`}
                          className={isSubUnitActive ? 'active' : ''}
                          onClick={() => navigateToPage(subUnit.start)}
                        >
                          <FolderOutlined sx={{ fontSize: 16, marginRight: 1 }} />
                          <ListItemText 
                            primary={subUnit.label || `Subunit ${subIndex + 1}`}
                            secondary={`Pages ${subUnit.start}-${subUnit.finish}`}
                          />
                        </SubNavigationItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <NavigationDrawer
        anchor="left"
        open={navigationOpen}
        onClose={() => setNavigationOpen(false)}
      >
        <NavigationHeader>
          <MenuBook />
          Book Navigation
        </NavigationHeader>
        {renderNavigationMenu()}
      </NavigationDrawer>
    </>
  );
};

export default BookNavigation;
